import styled from 'styled-components';
import type { TypographyProps } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { ellipsis, formatDate } from '../../../../helpers';
import { RssButtons } from '../../../buttons';
import type { ExtendedAudioItem } from '../../../sources';
import {
  Author as CommonAuthor,
  StyledAuthor as CommonStyledAuthor
} from '../Author';
import { Ticker as CommonTicker } from '../Ticker';

interface AuthorProps {
  textStyle: TypographyProps['textStyle'];
  isBarLayout: boolean;
  isInPlaylist: boolean;
  disableTicker: boolean;
  track?: ExtendedAudioItem;
}

export function Author({
  textStyle,
  isBarLayout,
  isInPlaylist,
  disableTicker,
  track
}: AuthorProps) {
  const { dateFormat = 'DD/MM/YYYY', trackElements = [] } = useSettings();
  const { author, hasAuthor, date } = track ?? {};
  const hasDate = trackElements.includes('date') && !!date;

  if (!hasAuthor && !hasDate) {
    return null;
  }

  if (isInPlaylist) {
    const formattedDate = hasDate ? formatDate(date, dateFormat) : undefined;

    return (
      <AuthorDateContainer>
        {hasAuthor && (
          <CommonStyledAuthor textStyle={textStyle} _disableTicker>
            {author}
          </CommonStyledAuthor>
        )}

        {hasDate && <Date dateTime={formattedDate}>{formattedDate}</Date>}
      </AuthorDateContainer>
    );
  }

  if (isBarLayout) {
    return (
      <Ticker disabled={disableTicker}>
        <StyledRssButtons />

        <StyledAuthor textStyle={textStyle} _disableTicker={disableTicker}>
          {author}
        </StyledAuthor>
      </Ticker>
    );
  }

  return (
    <CommonAuthor textStyle={textStyle} disableTicker={disableTicker}>
      {author}
    </CommonAuthor>
  );
}

const AuthorDateContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'podcast-track-info',
    element: 'author-date-container'
  })
})`
  display: inline-flex;
  align-items: center;
  width: 100%;
  margin-right: 8px;
`;

const Date = styled.time.attrs({
  className: createReadableClass({
    component: 'podcast-track-info',
    element: 'author-date'
  })
})`
  font-size: 13px;
  line-height: 16px;
  margin-top: 2px;
  opacity: 0.7;
  display: inline-block;
  margin-right: 8px;
  ${ellipsis};

  &:before {
    content: '';
    width: 2px;
    height: 2px;
    background-color: currentColor;
    vertical-align: middle;
    display: inline-block;
    margin: 0 8px;
    border-radius: 50%;
  }

  &:first-child:before {
    display: none;
  }
`;

const Ticker = styled(CommonTicker).attrs({
  className: createReadableClass({
    component: 'podcast-track-info',
    element: 'author-ticker'
  })
})`
  margin-top: 2px;

  & > div {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  & > div > div {
    vertical-align: middle;
  }
`;

const StyledAuthor = styled(CommonStyledAuthor).attrs({
  className: createReadableClass({
    component: 'podcast-track-info',
    element: 'author'
  })
})`
  display: inline-block;
`;

const StyledRssButtons = styled(RssButtons).attrs({
  className: createReadableClass({
    component: 'podcast-track-info',
    element: 'author-rss-buttons'
  })
})`
  display: inline-flex;
  flex-wrap: nowrap;
`;
